import { IonModal } from "@ionic/react";
import { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { sendEmail } from "../../services/email.service";

interface Props {
  isOpen: boolean;
  onDismiss(): void;
  user?: any;
  study?: any;
}

const ReportBugModal: FC<Props> = ({ isOpen, onDismiss, user, study }) => {
  const { t } = useTranslation();
  const [email, setEmail] = useState(user?.attributes?.email || "");
  const [description, setDescription] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async () => {
    if (!email.trim() || !description.trim()) {
      setError(t("common_all_fields_required"));
      return;
    }

    setError(null);
    setLoading(true);

    try {
      const emailData = {
        template: "USERSEQUENCENOTIFICATION",
        from: "noreply@mentalplus.fr",
        to: ["hosseinjamshidi87@gmail.com"],
        lang: "FR",
        params: {
          url: "https://app.mentalplus.fr",
          FRONT_URL: "https://app.mentalplus.fr/mentalplus",
          body: `<pre>
Bug Description: ${description}
User Email: ${email}
User ID: ${user?.id ?? "N/A"}
Study ID: ${study?.id ?? "N/A"}
          </pre>`,
          title: "Bug report",
        },
        studyId: study?.id,
      };

      const response = await sendEmail(emailData);
      console.log("Email sent successfully:", response.data);
      setSuccess(true);

      // Clear form after short delay
      setTimeout(() => {
        setSuccess(false);
        setDescription("");
        onDismiss();
      }, 1500);
    } catch (error) {
      console.error("Error sending bug report email:", error);
      setError(t("common_error_occurred"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <IonModal
      className="resonance-modal"
      isOpen={isOpen}
      backdropDismiss={true}
      onWillDismiss={onDismiss}
    >
      <Container onClick={onDismiss}>
        <Card onClick={(e) => e.stopPropagation()}>
          <Title>{t("profile_report_bug")}</Title>

          {success ? (
            <Success>{t("common_thanks_bug_report")}</Success>
          ) : (
            <>
              <Form>
                <Label>{t("common_your_email")}</Label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
                <Label>{t("common_bug_description")}</Label>
                <Textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") e.stopPropagation();
                  }}
                />
                {error && <Error>{error}</Error>}
              </Form>

              <Buttons>
                <ModalButton onClick={onDismiss}>
                  {t("common_cancel")}
                </ModalButton>
                <ModalButton
                  onClick={handleSubmit}
                  style={{ opacity: loading ? 0.6 : 1 }}
                >
                  {loading ? t("common_sending") + "..." : t("common_submit")}
                </ModalButton>
              </Buttons>
            </>
          )}
        </Card>
      </Container>
    </IonModal>
  );
};

// --- styled components remain unchanged except adding Success ---
const Container = styled.div`
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const Card = styled.div`
  width: 100%;
  max-width: 400px;
  background-color: white;
  border-radius: 22px;
  padding: 20px;
`;

const Title = styled.div`
  font-size: 18px;
  font-weight: bold;
  text-align: center;
  margin-bottom: 10px;
`;

const Success = styled.div`
  color: green;
  text-align: center;
  font-weight: bold;
  margin: 30px 0;
`;

const Form = styled.div`
  margin-top: 20px;
`;

const Label = styled.label`
  display: block;
  font-size: 14px;
  margin-bottom: 5px;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px;
  margin-bottom: 15px;
  border: 1px solid #ccc;
  border-radius: 5px;
  font-size: 14px;
`;

const Textarea = styled.textarea`
  width: 100%;
  height: 150px;
  padding: 10px;
  border: 1px solid #ccc;
  border-radius: 5px;
  font-size: 14px;
  resize: vertical;
 overflow-y: auto; /* Enable scrolling */
  background-color: white; /* Explicit background color */
  color: black; /* Explicit text color */
  &:focus {
    outline: none;
    border-color: ${(props) => props.theme.primary.main};
  }
  `;

const Error = styled.div`
  color: red;
  font-size: 12px;
  margin-top: 5px;
`;

const Buttons = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
`;

const ModalButton = styled.div`
  padding: 10px 20px;
  color: ${(props) => props.theme.primary.main};
  font-weight: bold;
  cursor: pointer;
  border: 1px solid ${(props) => props.theme.primary.main};
  border-radius: 5px;
  margin-left: 10px;
`;

export default ReportBugModal;
